// ConnectESPPage — shown post-signin when no active ESP integration exists.
// Rendered outside AppShell (no sidebar) — full-page centered card.
//
// Two distinct connect flows:
//   Mailchimp — paste-key: POST /api/v1/integrations/mailchimp/connect { api_key }
//   Brevo     — OAuth redirect: POST /v1/oauth/brevo/start → { authorize_url }
//
// IntegrationGate re-evaluates after return and passes through to dashboard.

import { useState } from "react";
import { KeyRound, Mail, ArrowRight, CheckCircle2 } from "lucide-react";
import { ApiError, apiFetch } from "@avagency/frontend/lib/api";
import { Button } from "@avagency/frontend/components/ui/button";
import { Footer } from "../components/Footer";

// ── Mailchimp: paste-key flow ─────────────────────────────────

async function connectMailchimp(apiKey: string): Promise<void> {
  await apiFetch<unknown>("/api/v1/integrations/mailchimp/connect", {
    method: "POST",
    json: { api_key: apiKey },
  });
}

function MailchimpForm({
  disabled,
  onSuccess,
  onPendingChange,
}: {
  disabled: boolean;
  onSuccess: () => void;
  /** Called with true when POST starts, false when it fails — lets parent disable Brevo. */
  onPendingChange: (pending: boolean) => void;
}) {
  const [key, setKey] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!key.trim()) return;
    setPending(true);
    onPendingChange(true);
    setError(null);
    try {
      await connectMailchimp(key.trim());
      onSuccess();
    } catch (err) {
      setPending(false);
      onPendingChange(false);
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Failed to connect Mailchimp. Please check your API key.");
      }
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" noValidate>
      <div className="space-y-2">
        <label
          htmlFor="mc-api-key"
          className="block text-xs font-medium"
          style={{
            color: "var(--yg-silver-3)",
            fontFamily: "var(--yg-font-mono)",
            letterSpacing: "0.14em",
            textTransform: "uppercase",
          }}
        >
          Mailchimp API key
        </label>
        <input
          id="mc-api-key"
          type="password"
          autoComplete="off"
          value={key}
          onChange={(e) => setKey(e.target.value)}
          placeholder="xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx-us1"
          className="w-full rounded-xl border px-4 py-3 text-sm font-mono"
          style={{
            background: "rgba(255,255,255,0.03)",
            borderColor: "var(--yg-line-strong)",
            color: "var(--yg-silver-0)",
            outline: "none",
          }}
          disabled={disabled || pending}
          aria-describedby={error ? "mc-error" : "mc-hint"}
        />
        <p
          id="mc-hint"
          className="text-xs"
          style={{ color: "var(--yg-silver-3)" }}
        >
          Find it under Account → Extras → API Keys.
        </p>
      </div>
      {error && (
        <p
          id="mc-error"
          role="alert"
          className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm"
          style={{
            color: "#ff8a8a",
            background: "rgba(255,138,138,0.06)",
            border: "1px solid rgba(255,138,138,0.18)",
          }}
        >
          {error}
        </p>
      )}
      <Button
        type="submit"
        className="w-full"
        disabled={disabled || pending || !key.trim()}
        aria-busy={pending}
      >
        {pending ? (
          <>Connecting…</>
        ) : (
          <>
            Connect Mailchimp
            <ArrowRight className="h-4 w-4 ml-1" aria-hidden="true" />
          </>
        )}
      </Button>
    </form>
  );
}

// ── Brevo: OAuth redirect flow ────────────────────────────────

async function startBrevoOAuth(): Promise<void> {
  const result = await apiFetch<{ authorize_url: string }>(
    "/v1/oauth/brevo/start",
    { method: "POST", json: { return_url: "/" } },
  );
  window.location.href = result.authorize_url;
}

// ── Page ──────────────────────────────────────────────────────

export function ConnectESPPage() {
  const [activeProvider, setActiveProvider] = useState<
    "mailchimp" | "brevo" | null
  >(null);
  const [brevoError, setBrevoError] = useState<string | null>(null);

  async function handleBrevoConnect() {
    setActiveProvider("brevo");
    setBrevoError(null);
    try {
      await startBrevoOAuth();
      // Navigation happens inside; no cleanup needed.
    } catch (err) {
      setActiveProvider(null);
      if (err instanceof ApiError) {
        setBrevoError(err.message);
      } else {
        setBrevoError("Failed to start Brevo OAuth. Please try again.");
      }
    }
  }

  return (
    <div className="flex min-h-svh flex-col items-center justify-center gap-6 p-4">
      {/* Header */}
      <div className="text-center">
        <div
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-6 text-xs"
          style={{
            background: "rgba(96,165,250,0.08)",
            border: "1px solid rgba(96,165,250,0.20)",
            color: "var(--yg-blue)",
            fontFamily: "var(--yg-font-mono)",
            letterSpacing: "0.14em",
            textTransform: "uppercase",
          }}
        >
          <CheckCircle2 className="h-3 w-3" aria-hidden="true" />
          Signed in
        </div>
        <h1
          className="text-2xl font-bold"
          style={{
            color: "var(--yg-silver-0)",
            letterSpacing: "-0.025em",
            fontFamily: "var(--yg-font-sans)",
          }}
        >
          Connect your ESP
        </h1>
        <p
          className="mt-2 text-sm"
          style={{ color: "var(--yg-silver-2)", lineHeight: "1.6" }}
        >
          Link Mailchimp or Brevo to start auditing your sending domains.
        </p>
      </div>

      <div className="w-full max-w-md space-y-4">
        {/* Mailchimp card */}
        <div className="yg-connect-card">
          <div className="flex items-center gap-3 mb-5">
            <div className="yg-provider-icon" aria-hidden="true">
              <KeyRound className="h-5 w-5" />
            </div>
            <div>
              <div
                className="font-semibold text-sm"
                style={{ color: "var(--yg-silver-0)", letterSpacing: "-0.01em" }}
              >
                Mailchimp
              </div>
              <div
                className="text-xs mt-0.5"
                style={{ color: "var(--yg-silver-3)" }}
              >
                API key authentication
              </div>
            </div>
          </div>
          <MailchimpForm
            disabled={activeProvider === "brevo"}
            onPendingChange={(p) =>
              setActiveProvider(p ? "mailchimp" : null)
            }
            onSuccess={() => {
              // IntegrationGate query will re-evaluate on next render;
              // trigger a soft navigation to allow the gate to redirect.
              window.location.replace("/");
            }}
          />
        </div>

        {/* Separator */}
        <div className="flex items-center gap-4">
          <div className="flex-1" style={{ height: "1px", background: "var(--yg-line)" }} />
          <span
            className="text-xs"
            style={{ color: "var(--yg-silver-3)", fontFamily: "var(--yg-font-mono)", letterSpacing: "0.12em" }}
          >
            OR
          </span>
          <div className="flex-1" style={{ height: "1px", background: "var(--yg-line)" }} />
        </div>

        {/* Brevo card */}
        <div className="yg-connect-card">
          <div className="flex items-center gap-3 mb-5">
            <div className="yg-provider-icon" aria-hidden="true">
              <Mail className="h-5 w-5" />
            </div>
            <div>
              <div
                className="font-semibold text-sm"
                style={{ color: "var(--yg-silver-0)", letterSpacing: "-0.01em" }}
              >
                Brevo
              </div>
              <div
                className="text-xs mt-0.5"
                style={{ color: "var(--yg-silver-3)" }}
              >
                OAuth 2.0 authorization
              </div>
            </div>
          </div>
          {brevoError && (
            <p
              role="alert"
              className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm mb-4"
              style={{
                color: "#ff8a8a",
                background: "rgba(255,138,138,0.06)",
                border: "1px solid rgba(255,138,138,0.18)",
              }}
            >
              {brevoError}
            </p>
          )}
          <Button
            className="w-full"
            variant="outline"
            disabled={activeProvider !== null}
            onClick={handleBrevoConnect}
            aria-busy={activeProvider === "brevo"}
          >
            {activeProvider === "brevo" ? (
              "Redirecting…"
            ) : (
              <>
                Connect Brevo via OAuth
                <ArrowRight className="h-4 w-4 ml-1" aria-hidden="true" />
              </>
            )}
          </Button>
        </div>
      </div>

      <Footer />
    </div>
  );
}
